// @flow
import React from 'react';
import {
  defineMessages,
  FormattedMessage,
  injectIntl,
  type IntlShape,
} from 'react-intl';
import type { InputProps } from 'redux-form';
import Button from 'brastrap/common/button/Button';
import Link from 'brastrap/common/link/Link';
import { filterInputProps } from 'brastrap/common/field-input/FieldInput';

export type SearchProps = {
  onMegaMenuClose: Function,
  active: boolean,
  focused: boolean,
  intl: IntlShape,
  onClearSearchPress: (SyntheticEvent<HTMLButtonElement>) => void,
  handleSubmit: (event?: SyntheticEvent<HTMLButtonElement>) => void,
  fields: {
    searchPhrase: InputProps,
  },
  onSearchBlur: (SyntheticEvent<HTMLInputElement>) => void,
  onSearchFocus: (SyntheticEvent<HTMLInputElement>) => void,
  onSearchKeyUp: (SyntheticEvent<HTMLInputElement>) => void,
  recentSearches: Array<{
    id: string,
    label: string,
    url: string,
  }>,
  suggestions: Array<{
    id: string,
    label: string,
    url: string,
  }>,
  isForMobile: boolean,
  onCloseDrawer: Function,
};

const messages = defineMessages({
  searchTitle: {
    id: 'header.search.main.title',
    defaultMessage: 'Product search',
  },
  placeholder: {
    id: 'header.search.input.placeholder',
    defaultMessage: 'Search by name, code or style',
  },
  searchAction: { id: 'header.search.action', defaultMessage: 'Go' },
  searchSuggestions: {
    id: 'header.search.suggestions.title',
    defaultMessage: 'Suggestions',
  },
  recentSearches: {
    id: 'header.search.recent.title',
    defaultMessage: 'Recent searches',
  },
  recentSearchesClear: {
    id: 'header.search.recent.clear.title',
    defaultMessage: 'Clear this list',
  },
});

const Search = (props: SearchProps) => {
  const displaySuggestions = props.suggestions.length > 0;
  const displayRecentSearches = props.recentSearches.length > 0;
  let suggestionsTabIndex = -1;

  const magGlassColour = (isMobile, searchPhrase) => {
    if (!isMobile && searchPhrase !== '') {
      return '#F4436C';
    } else if (isMobile && searchPhrase !== '') {
      return '#FFFFFF';
    } else if (!isMobile) {
      return '#616365';
    }
    return '#000000';
  };

  if (props.active) {
    suggestionsTabIndex = 0;
  }

  const handleSubmitForm = e => {
    e.preventDefault();
    props.handleSubmit();
    props.onMegaMenuClose();
  };
  return (
    <form
      onSubmit={handleSubmitForm}
      className={`c-header__search`}
      id="product-search"
      role="search"
    >
      <fieldset className="c-header__search__main">
        <label className="u-hidden" htmlFor="product-search">
          <FormattedMessage {...messages.searchTitle} />
        </label>

        <div
          className={`${
            props.isForMobile
              ? 'c-header__search__container--mobile'
              : 'c-header__search__container'
          }`}
        >
          <input
            {...filterInputProps(props.fields.searchPhrase)}
            className={`c-header__search__input
            ${props.isForMobile ? 'c-header__search__input--mobile' : ''}`}
            onBlur={props.onSearchBlur}
            onFocus={props.onSearchFocus}
            onKeyUp={props.onSearchKeyUp}
            placeholder={props.intl.formatMessage(messages.placeholder)}
            autoComplete="on"
            autoCorrect="on"
            type="search"
            aria-label="Product Search"
          />
          <button
            onClick={() => {
              props.onCloseDrawer();
            }}
            className={`${props.isForMobile ? 'c-header__search-button ' : ''}${
              props.isForMobile && props.fields.searchPhrase.value
                ? 'c-header__search-button--mobile'
                : ''
            }`}
            type="submit"
            aria-label={props.intl.formatMessage(messages.searchAction)}
          >
            <svg
              className="c-header__search__icon"
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 240 240"
              focusable="false"
              aria-hidden="true"
            >
              <path
                fillRule="evenodd"
                fill={magGlassColour(
                  props.isForMobile,
                  props.fields.searchPhrase.value
                )}
                d="M186 173l51 51c4 4 4 10 0 14-4 4-10 4-14 0L173 187c-18 15-42 25-67 25C47 211 0 163 0 106 0 48 48 0 106 0c58 0 106 48 106 106 0 25-9 49-25 67zM19 106c0-48 38-86 86-86 48 0 86 39 86 87S153 192 106 192c-48 0-86-39-86-87z"
              />
            </svg>
          </button>
        </div>
        <div
          className="c-popover c-header__search__suggestions"
          tabIndex={suggestionsTabIndex}
        >
          {displaySuggestions && (
            <section className="c-menu">
              <h1 className="c-menu__title">
                <FormattedMessage {...messages.searchSuggestions} />
              </h1>

              <ul className="c-menu__items">
                {props.suggestions.map(suggestion => (
                  <li key={suggestion.id} className="c-menu__item">
                    <Link
                      className="c-menu__label"
                      title={suggestion.label}
                      href="#suggestion"
                      rel="nofollow"
                    >
                      {suggestion.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          )}

          {displayRecentSearches && (
            <section className="c-menu">
              <h1 className="c-menu__title">
                <FormattedMessage {...messages.recentSearches} />
                <Button modifiers="small" onClick={props.onClearSearchPress}>
                  <FormattedMessage {...messages.recentSearchesClear} />
                </Button>
              </h1>

              <ul className="c-menu__items">
                {props.recentSearches.map(item => (
                  <li key={item.id} className="c-menu__item">
                    <Link
                      title={item.label}
                      className="c-menu__label"
                      href="#recentSearch"
                      rel="nofollow"
                    >
                      {item.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>
      </fieldset>
    </form>
  );
};

Search.defaultProps = {
  active: false,
  focused: false,
  onNavigationRequest: () => {},
  fields: { searchPhrase: {} },
  handleSubmit: () => {},
  onSearchBlur: () => {},
  onSearchFocus: () => {},
  onSearchKeyUp: () => {},
  recentSearches: [],
  suggestions: [],
};

export default injectIntl(Search);
